/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import TertiaryTopRoundButton from "../../../../components/_utils/CommonButtons/TertiaryTopRoundButton/TertiaryTopRoundButton";
import TopBarButtons from "../../../../components/_utils/CommonButtons/TopBarButton";
import CommonCardDashboard from "../../../../components/_utils/CommonCardDashboard";
import CommonMoreActionsCard from "../../../../components/_utils/CommonMoreActionsCard";
import WeeklySchedulesCard from "../../../../components/_utils/WeeklySchedulesCard";
import TransactionCounts from "../../../../components/Settings/Statistics/GraphsComponents/TransactionCounts";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import { internalTransactionsViewAll } from "../../../../redux/features/transactions/internalTransactions/index.js";
import { scheduledInternalViewAll } from "../../../../redux/features/transactions/scheduleInternalTransactions/index.js";
import {
  GlobalConfiguration,
  PaginationConfiguration,
  TimelineConfiguration,
} from "../../../../utils/configurationProvider";
import {
  default as globalText,
  default as TEXT,
} from "../../../../utils/globalText";

const InternalTransactionDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);

  const services = useSelector((state) => state.refreshToken.services);
  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);

  const {
    internalTransactionsViewAllData,
    internalTransactionsViewAllLoading,
  } = useSelector((state) => state?.internalTransactions);

  const { scheduledInternalViewData } = useSelector(
    (state) => state?.scheduleInternalTransactions,
  );

  const totalCount =
    internalTransactionsViewAllData?.total_count ??
    GlobalConfiguration.lengthCheckZero;
  const internalRequireApprovalTxnCount =
    internalTransactionsViewAllData?.summary?.actionable_transaction_count;

  const internalPendingTxnCount =
    internalTransactionsViewAllData?.summary?.pending_transaction_count;

  const internalApprovedTxnCount =
    internalTransactionsViewAllData?.summary?.approved_transaction_count;

  const [internalTxnCountLoading, setInternalTxnCountLoading] = useState(true);
  const [pendingScheduleCount, setPendingScheduleCount] = useState(0);

  const isViewAllDisabled =
    !services?.includes("VInternalTxn") ||
    internalTransactionsViewAllLoading ||
    internalTxnCountLoading ||
    totalCount === GlobalConfiguration?.lengthCheckZero;

  async function getInternalData() {
    await dispatch(
      internalTransactionsViewAll({
        page: 1,
        page_size: PaginationConfiguration.paginationSmall,
      }),
    );
    setInternalTxnCountLoading(false);
  }
  async function getScheduleData() {
    await dispatch(
      scheduledInternalViewAll({
        page: 1,
        page_size: PaginationConfiguration.paginationSmall,
      }),
    );
  }
  async function onMount() {
    setLoading(true);
    getInternalData();
    await getScheduleData();

    setLoading(false);
  }

  useEffect(() => {
    dispatch(enableSwitcher());

    const fetchPendingScheduleCount = async () => {
      try {
        const response = await dispatch(
          scheduledInternalViewAll({ lifecycle_status: "pending" }),
        );

        setPendingScheduleCount(response?.payload?.total_count || 0);
      } catch (error) {
        console.error("Error fetching pending schedule count:", error);
        setPendingScheduleCount(0);
      }
    };

    fetchPendingScheduleCount();

    return () => {
      dispatch(disableSwitcher());
    };
  }, []);

  useEffect(() => {
    (async () => {
      await onMount();
    })();
  }, [markerData]);

  const handleDatacardClick = (approvalStatus) => {
    const routeMap = {
      approved: "/internal_transaction_dashboard/internal_transaction_view_all",
      pending: "/internal_transaction_dashboard/internal_transaction_view_all",
      require:
        "/internal_transaction_dashboard/internal_transaction_single_approval",
    };

    const resolvedApprovalStatus = approvalStatus || "approved";

    navigate(routeMap[resolvedApprovalStatus], {
      state: {
        approvalStatus: resolvedApprovalStatus,
      },
    });
  };

  const rejectedTxnCount = +(
    internalTransactionsViewAllData?.summary?.rejected_transaction_count ?? "00"
  );

  // Function to check if a given date falls within the current week
  // TODO: Open to improvements — if anyone has a better or more accurate way to check weekly range, feel free to refactor.
  const isInThisWeek = (dateString) => {
    const date = dayjs(dateString);
    const startOfWeek = dayjs()?.startOf("week");
    const endOfWeek = dayjs()?.endOf("week");
    return (
      date?.isAfter(startOfWeek?.subtract(1, "day")) &&
      date?.isBefore(endOfWeek?.add(1, "day"))
    );
  };
  const thisWeekTransactions = Array?.isArray(
    scheduledInternalViewData?.transactions,
  )
    ? scheduledInternalViewData?.transactions?.filter((txn) =>
        isInThisWeek(txn?.transaction_start_date),
      )
    : [];
  const formatPendingCount = (count, services, serviceName) => {
    const hasService = (services || []).includes(serviceName);

    if (!hasService) {
      return count > GlobalConfiguration?.lengthCheckZero ? "N/A" : "00";
    }

    if (count === GlobalConfiguration?.lengthCheckZero) return "00";

    const digitCount = count.toString().length;

    // If 3 digits or less → show normally
    if (digitCount <= GlobalConfiguration?.categoryminLength) {
      return count
        .toLocaleString("en-IN")
        .padStart(TimelineConfiguration?.barWidthMedium, "0");
    }

    // Max number with one less digit
    const maxForDigits =
      Math.pow(
        GlobalConfiguration?.panLength,
        digitCount - GlobalConfiguration?.lengthCheckOne,
      ) - GlobalConfiguration?.lengthCheckOne;

    return `${maxForDigits.toLocaleString("en-IN")}+`;
  };
  const schedules = thisWeekTransactions;

  const safeServices = Array.isArray(services) ? services : [];

  const handleCreateSchedule = () => {
    if (
      safeServices.includes("ICScheduledTxn") &&
      safeServices.includes("IOScheduledTxn")
    ) {
      navigate("/internal_transaction_dashboard/create_schedule_transaction");
    } else {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: "ICScheduledTxn,IOScheduledTxn",
        },
      });
    }
  };

  const handleViewSchedules = () => {
    if (safeServices.includes("IVScheduledTxn")) {
      navigate("/internal_transaction_dashboard/schedule_view_all");
    } else {
      navigate("/feature_not_assigned", {
        state: { unavailableService: "IVScheduledTxn" },
      });
    }
  };

  /* REQUIRE APPROVAL */
  const handleRequireApprovalNavigation = () => {
    if (!loading)
      if (safeServices.includes("IAScheduledTxn")) {
        navigate("/internal_transaction_dashboard/schedule_single_approval");
      } else {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "IAScheduledTxn" },
        });
      }
  };
  const handleScheduleClick = (txnId) => {
    navigate(
      `/internal_transaction_dashboard/schedule_view_all/schedule_detail_page/${txnId}`,
      {
        state: { txnId },
      },
    );
  };

  return (
    <div
      style={{ height: "var(--full-height)" }}
      className={` internalDashboardView bg-primary-bg w-width-full px-padding-md overflow-y-auto lg:px-padding-base pb-padding-md`}
    >
      <div className="h-navbar-height Qsmd:h-navbar-breadcrumb-height"></div>
      <div className="pb-padding-xl">
        <div className="flex flex-col mt-margin-md lg:mx-auto pb-padding-xl lg:pb-padding-base max-w-tab-width-normal w-width-full Qlg:flex-row Qlg:px-padding-base lg:mb-margin-base Qlg:relative gap-gap-md lg:gap-0">
          <div className="w-width-full Qlg:w-dashboard-left-card-width flex flex-col">
            <div className="w-width-full p-padding-sm fixed top-top-base left-left-base right-right-base bg-text-primary flex items-center z-z-index-tooltip Qlg:hidden rounded-bl-border-radius-sm rounded-br-border-radius-sm">
              <div className="flex  items-center">
                <img
                  src={ICONS?.INTERNAL_TRANSACTION_BLUE_CIRCLE}
                  alt="INTERNAL_TRANSACTION_BLUE_CIRCLE"
                  className="h-icon-lg w-icon-lg mr-margin-sm"
                />
                <div className="flex flex-col">
                  <div className="flex items-center">
                    <p className="text-Blue-6 font-semibold text-base">
                      Internal
                    </p>
                  </div>
                  <p className="text-Blue-6 text-font-xs">Transactions</p>
                </div>
              </div>
            </div>

            <TopBarButtons
              services={services}
              items={[
                {
                  imgColor: "var(--text-primary)",
                  icon: ICONS?.ADD_BUTTON_WHITE,
                  label: <>{globalText.CREATE_INTERNAL_TRANSACTION}</>,
                  requiredservice: ["CInternalTxn"],
                  path: "/internal_transaction_dashboard/create_internal_transaction",
                  onClick: () => {
                    sessionStorage.removeItem("recreate_int_txn_id");
                  },
                },

                {
                  icon:
                    rejectedTxnCount > GlobalConfiguration.lengthCheckZero
                      ? ICONS?.ALERT
                      : ICONS?.ALERT_WHITE,
                  label: <>Recreate transactions</>,
                  requiredservice: ["VInternalTxn"],
                  count: rejectedTxnCount,
                  countLoading:
                    internalTxnCountLoading ||
                    internalTransactionsViewAllLoading,
                  path: "/internal_transaction_dashboard/recreate_internal_transaction_dashboard",
                },
              ]}
            />

            <div className="w-width-full bg-primary-card lg:mt-margin-md md:mt-margin-md sm:mt-margin-md mt-margin-md rounded-border-radius-md relative overflow-hidden">
              <h2 className="bg-primary-card flex items-center justify-between pl-padding-md text-text-secondary transition-all duration-animation-medium">
                <div className="flex items-center text-sm font-semibold">
                  <img
                    src={ICONS?.APPROVAL_STATUS}
                    alt="transaction header"
                    className="w-icon-sm h-icon-sm"
                  />
                  <p className="text-font-sm font-semibold font-sans text-text-primary ml-margin-sm">
                    {TEXT?.APPROVAL_STATUSES}
                  </p>
                </div>

                <div className="flex">
                  <div className="border-border my-margin-sm border-primary-bg"></div>
                  <TertiaryTopRoundButton
                    btnText={TEXT?.VIEW_ALL}
                    icon={ICONS?.ARROW_RIGHT_WHITE}
                    imgColor="var(--text-primary)"
                    btnType="button"
                    hoverOption="white"
                    handleFunction={() => {
                      const hasService = services?.includes("VInternalTxn");
                      if (!hasService) {
                        navigate("/feature_not_assigned", {
                          state: { unavailableService: "VInternalTxn" },
                        });
                        return;
                      }
                      if (isViewAllDisabled) return;
                      navigate(
                        "/internal_transaction_dashboard/internal_transaction_view_all",
                      );
                    }}
                    isDisabled={isViewAllDisabled}
                    enabledTextColor={"var(--text-primary)"}
                    enabledBgColor={"var(--primary-card)"}
                    disabledBgColor={"transparent"}
                  />
                </div>
              </h2>
              <div className="flex flex-col Qlg:flex-row gap-gap-base p-padding-base bg-primary-nav">
                {/* REQUIRE APPROVAL CARD */}
                <CommonCardDashboard
                  label={TEXT?.REQUIRE_APPROVAL}
                  count={
                    services?.includes("AInternalTxn")
                      ? (internalRequireApprovalTxnCount ?? "00")
                      : "N/A"
                  }
                  color="var(--pending)"
                  loader={
                    internalTxnCountLoading ||
                    internalTransactionsViewAllLoading
                  }
                  onClick={() =>
                    navigate(
                      "/internal_transaction_dashboard/internal_transaction_single_approval",
                    )
                  }
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "AInternalTxn" },
                    })
                  }
                />
                <div className="border-border border-primary-bg Qlg:my-margin-md my-margin-base" />
                <CommonCardDashboard
                  label={TEXT?.PENDING_TRANSACTIONS}
                  count={internalPendingTxnCount ?? "00"}
                  color="var(--pending)"
                  loader={
                    internalTxnCountLoading ||
                    internalTransactionsViewAllLoading
                  }
                  onClick={() => handleDatacardClick("pending")}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "VInternalTxn" },
                    })
                  }
                />
                <div className="border-border border-primary-bg Qlg:my-margin-md my-margin-base" />
                <CommonCardDashboard
                  label={TEXT?.APPROVED_TRANSACTIONS}
                  count={internalApprovedTxnCount ?? "00"}
                  color="var(--awaiting)"
                  loader={
                    internalTxnCountLoading ||
                    internalTransactionsViewAllLoading
                  }
                  onClick={() => handleDatacardClick("approved")}
                  onServiceUnavailable={() =>
                    navigate("/feature_not_assigned", {
                      state: { unavailableService: "VInternalTxn" },
                    })
                  }
                />
              </div>
            </div>

            <div className="w-full mt-margin-md bg-primary-nav rounded-t-border-radius-md rounded-b-border-radius-md">
              <div className="relative w-full overflow-hidden h-height-full">
                <div className={`relative w-full z-z-index-base`}>
                  <TransactionCounts
                    autoFetch={true}
                    flow="internal"
                    onDatacardClick={handleDatacardClick}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-y-gap-md w-width-full Qlg:w-dashboard-right-card-width lg:absolute Qlg:h-height-full Qlg:right-right-base">
            <WeeklySchedulesCard
              header={{
                title: "Schedules for this week",
                icon: ICONS.CALENDER,
              }}
              approval={{
                service: "IAScheduledTxn",
                count: pendingScheduleCount,
                label: "Require approval",
                onClick: handleRequireApprovalNavigation,
                isDisabled: loading,
              }}
              list={{
                loading: loading,
                emptyText: (
                  <>
                    There are no transactions scheduled for <br /> this week.
                  </>
                ),
                data: schedules,
                getTitle: (row) => row.scheduled_name,
                getID: (row) => row?.txn_id,
                getDate: (row) =>
                  dayjs(row.transaction_start_date).format("DD MMM YYYY"),
                titleLabel: "Schedule name",
                dateLabel: "Transaction initiation date",
                handleClick: handleScheduleClick,
              }}
              services={services}
              GlobalConfiguration={GlobalConfiguration}
              formatPendingCount={formatPendingCount}
              onFeatureNotAssigned={(missingServices) =>
                navigate("/feature_not_assigned", {
                  state: { unavailableService: missingServices },
                })
              }
              actions={[
                {
                  label: "Create schedule",
                  service: ["ICScheduledTxn", "IOScheduledTxn"],
                  onClick: handleCreateSchedule,
                  enabledBgColor: "var(--primary-nav-act)",
                  enabledTextColor: "var(--text-primary)",
                  hoverOption: "white",
                  fullWidthRequired: true,
                },
                {
                  label: "View schedules",
                  service: "IVScheduledTxn",
                  onClick: handleViewSchedules,
                  enabledBgColor: "var(--primary-nav-act)",
                  enabledTextColor: "var(--text-primary)",
                  hoverOption: "white",
                  fullWidthRequired: true,
                },
              ]}
            />

            <div className="w-full">
              <CommonMoreActionsCard
                title="More actions"
                actions={[
                  {
                    label: "MIS report",
                    service: "SMisUser",
                    onClick: () => {
                      const dashboardBasePath = location.pathname.includes(
                        "internal_transaction_dashboard",
                      )
                        ? "/internal_transaction_dashboard"
                        : "/beneficiary_transaction_dashboard";

                      if (services?.includes("SMisUser")) {
                        navigate(`${dashboardBasePath}/generate_mis_report`);
                      } else {
                        navigate("/feature_not_assigned", {
                          state: { unavailableService: "SMisUser" },
                        });
                      }
                    },
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalTransactionDashboard;
